import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import { articleMeta } from "@/mocks/articleMeta";
import { articlesEn } from "@/mocks/articlesEn";
import { articlesFr } from "@/mocks/articlesFr";
import { articlesEs } from "@/mocks/articlesEs";
import { articlesPl } from "@/mocks/articlesPl";
import { categories, MAIN_SITE_URL } from "@/mocks/categories";
import type { ArticleData } from "@/types/article";
import { SITE_URL, SITE_LOGO } from "@/lib/seo";

const articlesByLang: Record<string, Record<string, ArticleData>> = {
  en: articlesEn,
  fr: articlesFr,
  es: articlesEs,
  pl: articlesPl,
};

function resolveArticle(slug: string, lang: string): ArticleData | undefined {
  const langArticles = articlesByLang[lang] || articlesEn;
  return langArticles[slug] || articlesEn[slug];
}

export default function ArticlePage() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || "en").split("-")[0];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug, lang]);

  const data = slug ? resolveArticle(slug, lang) : undefined;

  if (!data) {
    return <Navigate to="/" replace />;
  }

  const currentImage = articleMeta.find((m) => m.slug === data.slug)?.image;

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: t("nav_home"), item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: data.title, item: `${SITE_URL}/articles/${data.slug}` },
    ],
  };

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: data.title,
    description: data.description,
    image: currentImage,
    inLanguage: lang,
    dateModified: data.updated,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `${SITE_URL}/articles/${data.slug}`,
    },
    publisher: {
      "@type": "Organization",
      name: "USFansLink.study",
      logo: {
        "@type": "ImageObject",
        url: SITE_LOGO,
      },
    },
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: data.faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const relatedArticles = articleMeta.filter((m) => m.slug !== data.slug).slice(0, 3);

  return (
    <div className="min-h-screen bg-background-50">
      <Header />
      <main className="pt-16">
        <article className="w-full px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-[800px] mx-auto">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-xs text-foreground-500 flex-wrap" aria-label="Breadcrumb">
              <Link to="/" className="cursor-pointer hover:text-primary-600 transition-colors whitespace-nowrap">
                {t("nav_home")}
              </Link>
              <i className="ri-arrow-right-s-line" />
              <span className="text-foreground-700 whitespace-nowrap">{data.title}</span>
            </nav>

            {/* Title */}
            <h1 className="mt-6 text-2xl md:text-4xl font-heading font-bold text-foreground-950 leading-tight">
              {data.title}
            </h1>
            <p className="mt-4 text-sm text-foreground-500">
              {t("footer_disclaimer")} • {data.updated}
            </p>

            <p className="mt-6 text-base md:text-lg text-foreground-700 leading-relaxed">{data.intro}</p>

            {/* Sections */}
            <div className="mt-8 space-y-8">
              {data.sections.map((section, i) => (
                <section key={i}>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">{section.heading}</h2>
                  {section.paragraphs.map((p, j) => (
                    <p key={j} className="mt-3 text-sm md:text-base text-foreground-700 leading-relaxed">
                      {p}
                    </p>
                  ))}
                </section>
              ))}
            </div>

            {/* Category links (internal linking / hub) */}
            <section className="mt-10">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
                {t("categories_title")}
              </h2>
              <div className="mt-4 flex flex-wrap gap-2">
                {categories.slice(0, 8).map((c) => (
                  <a
                    key={c.slug}
                    href={c.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-900 text-xs font-medium cursor-pointer hover:bg-secondary-200 transition-colors whitespace-nowrap"
                  >
                    {t(`cats.${c.slug}`)}
                  </a>
                ))}
              </div>
            </section>

            {/* FAQ */}
            <section className="mt-10">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">{data.faqTitle}</h2>
              <div className="mt-4 space-y-3">
                {data.faqs.map((f, i) => (
                  <details key={i} className="rounded-lg border border-background-200 bg-background-50 group">
                    <summary className="flex items-center justify-between gap-3 px-5 py-4 cursor-pointer text-sm md:text-base font-medium text-foreground-900">
                      {f.q}
                      <i className="ri-add-line text-foreground-400 group-open:hidden" />
                      <i className="ri-subtract-line text-foreground-400 hidden group-open:block" />
                    </summary>
                    <p className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">{f.a}</p>
                  </details>
                ))}
              </div>
            </section>

            {/* CTA */}
            <section className="mt-12 rounded-lg bg-primary-50 border border-primary-200 p-6 md:p-8 text-center">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">{data.ctaTitle}</h2>
              <p className="mt-3 text-sm text-foreground-700 max-w-xl mx-auto leading-relaxed">{data.ctaDesc}</p>
              <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href={MAIN_SITE_URL}
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 font-semibold text-sm cursor-pointer hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  {t("hero_cta_primary")}
                  <i className="ri-arrow-right-up-line" />
                </a>
                <Link
                  to="/"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md border border-foreground-300 text-foreground-900 font-semibold text-sm cursor-pointer hover:bg-background-50 transition-colors whitespace-nowrap"
                >
                  {data.backHome}
                </Link>
              </div>
            </section>

            {/* Related guides */}
            <section className="mt-10 border-t border-background-200 pt-8">
              <h2 className="text-lg font-heading font-bold text-foreground-950">{t("article_related")}</h2>
              <div className="mt-4 flex flex-col gap-3">
                {relatedArticles.map((m) => {
                  const relData = resolveArticle(m.slug, lang);
                  if (!relData) return null;
                  return (
                    <Link
                      key={m.slug}
                      to={`/articles/${m.slug}`}
                      className="group flex items-center gap-3 p-4 rounded-lg border border-background-200 bg-background-50 cursor-pointer hover:border-primary-300 transition-colors"
                    >
                      <img
                        src={m.image}
                        alt={relData.title}
                        className="w-16 h-16 rounded-md object-cover object-top shrink-0"
                      />
                      <span className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors">
                        {relData.title}
                      </span>
                      <i className="ri-arrow-right-line ml-auto text-foreground-400" />
                    </Link>
                  );
                })}
                <Link
                  to="/"
                  className="group flex items-center gap-3 p-4 rounded-lg border border-background-200 bg-background-50 cursor-pointer hover:border-primary-300 transition-colors"
                >
                  <span className="w-16 h-16 flex items-center justify-center rounded-md bg-primary-100 text-primary-600 shrink-0">
                    <i className="ri-home-5-line text-lg" />
                  </span>
                  <span className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors">
                    {t("nav_home")} — {t("brand")}
                  </span>
                  <i className="ri-arrow-right-line ml-auto text-foreground-400" />
                </Link>
              </div>
            </section>
          </div>
        </article>
      </main>
      <Footer />

      {/* Schema */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
    </div>
  );
}